import React, { useState } from "react";
import Icon from "../../../components/AppIcon";

const BACKEND_URL =
  import.meta.env.VITE_BACKEND_URL || "https://api.sherpatrekker.com";

function fixSrc(src) {
  if (!src) return "";
  return src.startsWith("/") ? `${BACKEND_URL}${src}` : src;
}

function labelFromSrc(src, fallback = "") {
  if (!src) return fallback;
  if (!src.startsWith("/")) return fallback;
  const filename = src.split("/").pop() || "";
  const name = filename.replace(/\.[^.]+$/, "");
  return name
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

const Lightbox = ({ photos, index, onClose }) => {
  const [current, setCurrent] = useState(index);

  const prev = () => setCurrent((c) => (c === 0 ? photos.length - 1 : c - 1));
  const next = () => setCurrent((c) => (c === photos.length - 1 ? 0 : c + 1));

  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  React.useEffect(() => {
    const handler = (e) => {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const photo = photos[current];

  return (
    <div
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
      onClick={handleBackdrop}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-white/25 rounded-full flex items-center justify-center text-white transition-colors z-10"
        aria-label="Close"
      >
        <Icon name="X" size={20} />
      </button>

      <div className="absolute top-4 left-4 text-white/70 text-sm font-medium">
        {current + 1} / {photos.length}
      </div>

      <button
        onClick={prev}
        className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 bg-white/10 hover:bg-white/25 rounded-full flex items-center justify-center text-white transition-colors"
        aria-label="Previous photo"
      >
        <Icon name="ChevronLeft" size={22} />
      </button>

      <div className="max-w-5xl w-full mx-16">
        <img
          src={fixSrc(photo.src)}
          alt={photo.alt}
          className="w-full max-h-[80vh] object-contain rounded-lg"
        />
        {photo.alt && (
          <p className="text-white/60 text-sm text-center mt-3">{photo.alt}</p>
        )}
      </div>

      <button
        onClick={next}
        className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 bg-white/10 hover:bg-white/25 rounded-full flex items-center justify-center text-white transition-colors"
        aria-label="Next photo"
      >
        <Icon name="ChevronRight" size={22} />
      </button>
    </div>
  );
};

const Tile = ({ photo, onClick }) => (
  <div
    className="group relative overflow-hidden rounded-xl cursor-pointer bg-[#fafaf7]"
    style={{ aspectRatio: "1 / 1" }}
    onClick={onClick}
  >
    <img
      src={fixSrc(photo.src)}
      alt={photo.alt}
      loading="lazy"
      decoding="async"
      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
    />
    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300 flex items-center justify-center">
      <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <Icon name="ZoomIn" size={20} className="text-white drop-shadow-lg" />
      </div>
    </div>
  </div>
);

const PhotoGallery = ({ photos }) => {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  if (!photos?.length) return null;

  return (
    <div className="bg-white py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="section-heading text-foreground mb-3">
            <span className="text-accent">Photo</span> Gallery
          </h2>
          <div className="w-16 h-1 bg-accent rounded-full mx-auto mb-4" />
          <p className="text-lg text-text-secondary max-w-2xl mx-auto leading-relaxed">
            Experience the breathtaking beauty of the Himalayas through our
            curated collection of trek photos.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {photos.map((photo, i) => (
            <Tile key={photo.id ?? i} photo={photo} onClick={() => setLightboxIndex(i)} />
          ))}
        </div>

        <p className="text-center text-sm text-text-secondary mt-4">
          {photos.length} photos &middot; click any to enlarge
        </p>
      </div>

      {lightboxIndex !== null && (
        <Lightbox
          photos={photos}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
};

export default PhotoGallery;
